import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, type StatementDetail, type StatementView } from '../lib/api';
import { dateTime } from '../lib/format';
import { monthLabel } from '../lib/contracts';

// Etapa 2.3 · Estados de cuenta del contrato (en su detalle).

export const STATEMENT_STATUS = {
  draft: { label: 'Borrador', chip: 'bg-[#EDEBE7] text-muted' },
  sent: { label: 'Enviado al cliente', chip: 'bg-[#FFF4E5] text-[#6A3A00]' },
  in_clarification: { label: 'En aclaración', chip: 'bg-[#FBEAEA] text-[#8F1D1D]' },
  closed: { label: 'Cerrado', chip: 'bg-accent-soft text-accent-dark' }
} as const;

const lastMonths = (current: string, n = 6) => {
  const [y, m] = current.split('-').map(Number);
  return Array.from({ length: n }, (_, i) => new Date(Date.UTC(y!, m! - 2 - i, 1)).toISOString().slice(0, 7));
};

export function StatementsSection({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['statements', projectId], queryFn: () => api<{ statements: StatementView[]; currentMonth: string }>(`/contracts/${projectId}/statements`) });
  const [month, setMonth] = useState('');
  const gen = useMutation({
    mutationFn: (m: string) => api<StatementDetail>(`/contracts/${projectId}/statements`, { method: 'POST', body: JSON.stringify({ month: m }) }),
    onSuccess: d => navigate(`/contratos/${projectId}/estado/${d.statement.month}`)
  });
  const rows = q.data?.statements ?? [];
  const missing = q.data ? lastMonths(q.data.currentMonth).filter(m => !rows.some(r => r.month === m)) : [];

  return (
    <section className="rounded-lg border border-line bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Estados de cuenta</h2>
          <p className="mt-0.5 text-xs text-muted">El borrador del mes se crea el día 1. Enviado al cliente, se acepta solo si no pide aclaraciones en 5 días hábiles.</p>
        </div>
        {missing.length > 0 && (
          <span className="flex items-center gap-2">
            <select aria-label="Mes a generar" className="h-9 rounded-md border border-[#D6D3CD] bg-white px-2 text-sm" value={month} onChange={e => setMonth(e.target.value)}>
              <option value="">Generar de…</option>
              {missing.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
            <button type="button" disabled={!month || gen.isPending} onClick={() => gen.mutate(month)}
              className="inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm hover:bg-paper disabled:opacity-50">Generar</button>
          </span>
        )}
      </div>
      {gen.error && <p className="mt-2 text-sm text-[#8F1D1D]">{gen.error instanceof ApiError ? gen.error.message : 'No se pudo generar.'}</p>}
      {q.isLoading ? <p className="mt-3 text-sm text-muted">Cargando…</p> : rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted">Aún no hay estados de cuenta.</p>
      ) : (
        <ul className="mt-3 divide-y divide-[#EFEDE9] text-sm">
          {rows.map(s => (
            <li key={s.month}>
              <Link to={`/contratos/${projectId}/estado/${s.month}`} className="flex flex-wrap items-center justify-between gap-2 py-2.5 hover:text-accent">
                <span className="font-medium">{monthLabel(s.month)}</span>
                <span className="flex items-center gap-3 text-xs text-muted">
                  {s.openClarifications > 0 && <span className="text-[#8F1D1D]">{s.openClarifications} aclaración{s.openClarifications === 1 ? '' : 'es'}</span>}
                  {s.status === 'sent' && s.dueAt && <span>se acepta solo el {dateTime(s.dueAt)}</span>}
                  <span className={`rounded px-1.5 py-0.5 font-semibold ${STATEMENT_STATUS[s.status].chip}`}>{STATEMENT_STATUS[s.status].label}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
